import { format } from "date-fns";
import React, { memo, useRef, useState } from "react";
import { Modal, StyleSheet, TouchableOpacity, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@assets/svg/CloseIcon";
import FiltersIcon from "@assets/svg/FiltersIcon";
import Button from "@components/Button";
import Text from "@components/CustomText";
import DatePicker from "@components/DatePicker";
import RadioButton from "@components/RadioButton";
import colors from "@constants/color";
import { actions as walletActions } from "@reducers/oldWalletReducer";
import t from "@constants/localization";
import { getWalletSummary } from "../../services/api";

const WalletFilters = memo(
  ({ containerBgColor = "transparent", onApplyFilter = () => {} }) => {
    const [shouldFiltersVisible, setShouldFiltersVisible] = useState(false);
    const { filters, currentFilter, currentScreen, walletSummary } =
      useSelector((state) => state.wallet);
    const dateRef = useRef();
    const dispatch = useDispatch();
    const toggleFilters = () => {
      setShouldFiltersVisible(!shouldFiltersVisible);
    };

    const filterStr = walletSummary?.appliedFilter
      ? walletSummary?.appliedFilter?.split('"').filter(Boolean)
      : null;

    const getFormatedDate = (date) => {
      return format(new Date(date), "yyyy-MM-dd");
    };

    const handleFilterApply = () => {
      if (currentFilter.key === "custom") {
        const [from, to] = dateRef.current.getDates();
        const fromDate = getFormatedDate(from);
        const toDate = getFormatedDate(to);

        dispatch(
          walletActions.setCurrentFilter(currentFilter.key, {
            fromDate: from,
            toDate: to,
          }),
        );

        getWalletSummary({ to: toDate, from: fromDate })
          .then(({ data }) => {
            dispatch(walletActions.setWalletSummary(data));
            toggleFilters();
          })
          .catch((e) => {
            console.log(e);
          });

        onApplyFilter({ to: toDate, from: fromDate });
      } else {
        const { fromDate, toDate } = currentFilter;
        getWalletSummary({ to: toDate, from: fromDate })
          .then(({ data }) => {
            dispatch(walletActions.setWalletSummary(data));
            toggleFilters();
          })
          .catch((e) => {
            console.log(e);
          });
        onApplyFilter({ to: toDate, from: fromDate });
      }
    };

    function handleDateChanged(dateType, date) {
      const { fromDate = "", toDate = "" } = currentFilter;

      dispatch(
        walletActions.setCurrentFilter(currentFilter.key, {
          fromDate: dateType.type === "fromDate" ? date : fromDate || null,
          toDate: dateType.type === "toDate" ? date : toDate || null,
        }),
      );
    }

    return (
      <>
        <View
          style={[
            styles.beLikeRow,
            styles.filterContainer,
            {
              backgroundColor: containerBgColor,
              justifyContent: "space-between",
              width: "100%",
            },
          ]}
        >
          <Text variant="body_text_3" style={styles.secondaryText}>
            <Text variant="body_text_4">
              “{filterStr?.[0]?.trim?.() ?? ""}”
            </Text>
            {` ${filterStr?.[1]?.trim?.() ?? ""}`}
          </Text>
          <TouchableOpacity style={styles.beLikeRow} onPress={toggleFilters}>
            <FiltersIcon />
            <Text
              variant="body_text_4"
              style={{ marginLeft: 3, color: "rgba(0, 0, 0, 0.86)" }}
            >
              {t("common/filter")}
            </Text>
          </TouchableOpacity>
        </View>
        <Modal
          statusBarTranslucent
          navigationBarTranslucent
          transparent
          visible={shouldFiltersVisible}
          onRequestClose={toggleFilters}
          animationType="fade"
        >
          <View
            style={{
              flex: 1,
              ...StyleSheet.absoluteFill,
              backgroundColor: "rgba(0, 0, 0, 0.56)",
            }}
          >
            <View
              style={{
                backgroundColor: "#fff",
                position: "absolute",
                bottom: 0,
                width: "100%",
                paddingVertical: 20,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
              }}
            >
              <View
                style={[
                  styles.beLikeRow,
                  styles.headContainer,
                  {
                    justifyContent: "space-between",
                    marginVertical: 5,
                    paddingHorizontal: 10,
                  },
                ]}
              >
                <View style={{ paddingHorizontal: 10 }}>
                  <Text variant="body_text_4" fontType="head3">
                    {t("common/filter")}
                  </Text>
                </View>

                <TouchableOpacity
                  onPress={() => {
                    toggleFilters();
                  }}
                >
                  <CloseIcon fillOpacity={0.7} width={24} height={24} />
                </TouchableOpacity>
              </View>
              <View style={{ marginTop: 5 }}>
                <RadioButton
                  radioOptions={filters}
                  setSelectedOption={(key) => {
                    if (currentFilter.key === key) return;
                    dispatch(walletActions.setCurrentFilter(key));
                  }}
                  showRadioInLast
                  wrapperStyles={{
                    justifyContent: "space-between",
                    borderBottomWidth: 1,
                    borderBottomColor: "rgba(0, 0, 0, 0.1)",
                  }}
                  hindiText
                  labelColor="rgba(0, 0, 0, 0.87)"
                  radioColor={colors.primary}
                  active={currentFilter.key}
                />
              </View>
              <View
                style={{
                  paddingHorizontal: 10,
                }}
              >
                {currentFilter.key === "custom" && (
                  <DatePicker
                    ref={dateRef}
                    dates={
                      currentFilter.toDate || currentFilter.fromDate
                        ? {
                            toDate: currentFilter.toDate,
                            fromDate: currentFilter.fromDate,
                          }
                        : {}
                    }
                    handleDateChanged={handleDateChanged}
                  />
                )}
                <View style={{ marginTop: 40 }}>
                  <Button
                    borderRadius={10}
                    btnText={t("common/apply")}
                    buttonWidth="100%"
                    height={56}
                    shouldBoldText
                    buttonFontType="head3"
                    onPress={handleFilterApply}
                  />
                </View>
              </View>
            </View>
          </View>
        </Modal>
      </>
    );
  },
);

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterContainer: {
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: "transparent",
    borderBottomColor: "#ddd",
    padding: 10,
  },
  secondaryText: {
    color: "rgba(0, 0, 0, 0.56)",
  },
});

export default WalletFilters;
